import { useCallback, useEffect, useState, useRef } from 'react';
import dynamic from 'next/dynamic';
import { tw, apply } from 'twind/style';
import { Icon, IconProps } from '@global-ecom/nitro-uds/elements';

import { ProductImage, usePixleeConfigQuery } from '__generated__/graphql';
import type {
  ModalStateType,
  GalleryItemType,
} from 'ui/pages/ProductDetailsPages/types';
import { Img } from 'ui/components/Img';
import { CarouselModalProps, isVideo } from 'ui/components/CarouselModal';
import { ProductVideo, Video } from 'ui/components/Video';
import { ResponsiveProductImage } from 'ui/components/ResponsiveProductImage';
import { CompleteTheLookBadge } from 'ui/components/StyliticsWidget/CompleteTheLookBadge';
import { useTranslate } from 'hooks/useTranslations';
import { usePdp } from 'hooks/usePdp';
import { useSiteConfig } from 'hooks/useSiteConfig';
import {
  AnalyticsEvents,
  addPrefixToAnalyticProp,
  event,
  transformImageUrlToImageInteractionAnalyticsEvent,
} from 'utils/analytics';
import { SaleBadge } from 'ui/components/ProductBadge';
import { useGA4Events } from 'hooks/useGA4Events';
import { usePageEventsContext } from 'hooks/usePageEventsContext';
import { debounce } from 'utils/debounce';

import { ProductVideoMuteButton } from './ProductVideoMuteButton';

const CarouselModal = dynamic<CarouselModalProps>(
  () => import('ui/components/CarouselModal').then(x => x.CarouselModal),
  {
    ssr: false,
  }
);

export const ProductImageGallery = ({ className }: { className?: string }) => {
  const translate = useTranslate();
  const {
    pdpVideosConfig,
    staticFeatures: { useVerticalMedia, isPNARegion },
  } = useSiteConfig();
  const {
    pixleeImgs,
    masterProduct,
    isFullLayout,
    isSimplifiedLayout,
    variation,
  } = usePdp();
  const [variationId, setVariationId] = useState(variation?.variantId);
  const [isVideoMuted, setIsVideoMuted] = useState(
    pdpVideosConfig.productVideoOptions?.muted ?? true
  );
  const [modalState, setModalState] = useState<ModalStateType>({
    show: false,
    picIndex: 0,
    muted: isVideoMuted,
  });

  const [pixleeConfigResponse] = usePixleeConfigQuery();

  const { fireClickProductImage, fireEventGA4ForImageOpened } = useGA4Events();

  const pixleeConfig = pixleeConfigResponse.data?.pixleeConfig;

  const sliderRef = useRef<HTMLDivElement>(null);

  const renderMedia = useCallback(
    (media: GalleryItemType, picIndex: number) => {
      const isFirstImage = picIndex === 0;
      const dataTestId = isFirstImage
        ? 'pdp-main-image'
        : `img-${picIndex}-${media.__typename}`;

      if (isVideo(media) && pdpVideosConfig.enabled) {
        const video: ProductVideo = media as ProductVideo;

        return (
          <div
            key={video.src}
            className="relative flex-none w-full"
            data-test-id={dataTestId}
            onClick={() =>
              setModalState({
                show: true,
                picIndex,
                muted: isVideoMuted,
              })
            }
          >
            <ProductVideoMuteButton
              isMuted={isVideoMuted}
              setIsMuted={setIsVideoMuted}
              className={tw(
                'bg-white text-black rounded-full border-transparent absolute bottom-2 right-2 z-10 m-0 p-2'
              )}
            />
            <Video
              src={video.src}
              videoOptions={video.videoOptions}
              muted={isVideoMuted}
              priority={true}
            />
          </div>
        );
      } else {
        const image: ProductImage = media as ProductImage;
        const src = useVerticalMedia
          ? image.verticalImageHref || image.href
          : image.href;

        return (
          <div
            className="relative flex-none w-full aspect-1-1 bg-puma-black-800"
            key={src}
          >
            {isFirstImage && isPNARegion && <CompleteTheLookBadge />}
            <ResponsiveProductImage
              src={src}
              widths={[600, 750, 900, 1050, 1200, 1350, 1500, 1750, 2000]}
              sizes={{
                mobile: '100vw',
                tablet: isFirstImage ? '50vw' : '25vw',
                desktop: '33.3vw',
              }}
              isVertical={useVerticalMedia}
              alt={image.alt}
              data-test-id={dataTestId}
              loading={isFirstImage ? 'eager' : 'lazy'}
              onClick={() => {
                setModalState({ show: true, picIndex });
                event(AnalyticsEvents.PDP_INTERACTION, {
                  interactionType: 'Gallery Open',
                  ...transformImageUrlToImageInteractionAnalyticsEvent(src),
                });
                fireClickProductImage(src);
                fireEventGA4ForImageOpened({
                  itemListId: variation?.id,
                  itemListName: variation?.name,
                });
              }}
            />
          </div>
        );
      }
    },
    [
      pdpVideosConfig.enabled,
      isVideoMuted,
      useVerticalMedia,
      isPNARegion,
      fireClickProductImage,
      fireEventGA4ForImageOpened,
      variation?.id,
      variation?.name,
    ]
  );

  const videoOptions = pdpVideosConfig.productVideoOptions;
  const galleryItems: GalleryItemType[] = [];
  const showGalleryModal = modalState.show && variation && galleryItems;

  if (variation?.images) {
    galleryItems.push(
      ...variation?.images.map(i => ({ __typename: 'ProductImage', ...i }))
    );
  }

  const videosKey = useVerticalMedia
    ? 'verticalProductVideos'
    : 'productVideos';
  const masterProductVideos = masterProduct?.[videosKey];

  if (masterProductVideos && pdpVideosConfig.enabled) {
    const videos: GalleryItemType[] = masterProductVideos.map(
      src => ({ __typename: 'ProductVideo', src, videoOptions } as ProductVideo)
    );
    galleryItems.splice(
      pdpVideosConfig.videoPosition ? pdpVideosConfig.videoPosition : 0,
      0,
      ...videos
    );
  }

  useEffect(() => {
    if (showGalleryModal) setIsVideoMuted(true);
  }, [showGalleryModal]);

  useEffect(() => {
    if (variationId !== variation?.variantId && sliderRef.current) {
      setVariationId(variation?.variantId);
      sliderRef.current.scrollLeft = 0;
    }
  }, [variation?.variantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const baseImageContainerClasses = tw({
    '': 'relative flex whitespace-nowrap overflow-x-scroll scroll-snap',
    'md:':
      'grid gap-4 grid-cols-2 overflow-x-auto children:first-child:col-span-full',
    'lg:': 'children:first-child:col-span-1 grid-cols-2',
  });

  const { pageviewEventHasFired } = usePageEventsContext();
  const analyticsImage = transformImageUrlToImageInteractionAnalyticsEvent(
    variation?.preview || ''
  );
  const triggerEvent = () => {
    if (pageviewEventHasFired)
      event(AnalyticsEvents.GA4_CustomEvent, {
        event_name: AnalyticsEvents.IMAGE_VIEWED,
        event_params: {
          item_id_ep: variation?.id,
          item_name_ep: variation?.name,
          color_code_ep: addPrefixToAnalyticProp(variation?.colorValue),
          style_number_ep: variation?.styleNumber,
          image_type: analyticsImage.imageType,
        },
      });
  };

  useEffect(() => {
    const handleScroll = debounce(() => {
      triggerEvent();
    }, 100);

    const scrollEventListenerRef = sliderRef.current;

    if (scrollEventListenerRef) {
      scrollEventListenerRef.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollEventListenerRef) {
        scrollEventListenerRef.removeEventListener('scroll', handleScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <section
        data-test-id="product-image-gallery-section"
        className="order-1 relative md:col-span-4 lg:col-span-8"
        aria-label={translate('productGallery')}
      >
        <SaleBadge variant={variation} />
        <div
          ref={sliderRef}
          className={tw([apply(baseImageContainerClasses), className])}
        >
          {galleryItems
            .slice(0, isSimplifiedLayout ? 1 : galleryItems.length)
            .map(renderMedia)}
          {isFullLayout &&
            pixleeImgs?.map((img, index) => {
              <div key={img.href} className="relative w-full flex-none">
                {img.source && pixleeConfig?.socialMediaIcon && (
                  <Icon
                    name={('social-' + img.source) as IconProps['name']}
                    size="3xl"
                    className="absolute top-3 left-3"
                  />
                )}
                <Img
                  src={img.href}
                  alt={img.alt}
                  width={400}
                  onClick={() =>
                    setModalState({
                      show: true,
                      picIndex: galleryItems.length + index,
                    })
                  }
                />
              </div>;
            })}
        </div>
      </section>
      {showGalleryModal && (
        <CarouselModal
          galleryItems={galleryItems}
          modalState={modalState}
          pixleeImages={pixleeImgs}
          isSocialIconVisible={!!pixleeConfig?.socialMediaIcon}
          onClose={() => {
            setModalState({
              show: false,
              picIndex: 0,
              muted: true,
            });
          }}
          triggerEvent={triggerEvent}
        />
      )}
    </>
  );
};
